import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, X, ArrowRight, Scale, Car, Building2, MapPin, BookOpen, FileText, MessageCircle, Calculator, Shield, DollarSign, Camera, AlertCircle, Home, Receipt, Settings, GraduationCap, AlertTriangle, Radio, Book, Send, Clipboard } from 'lucide-react';
import Fuse from 'fuse.js';

// Icon mapping for dynamic icon rendering
const ICONS = {
  Scale, Car, Building2, MapPin, BookOpen, FileText, MessageCircle, Calculator,
  Shield, DollarSign, Camera, AlertCircle, Home, Receipt, Settings, GraduationCap,
  AlertTriangle, Radio, Book, Send, Clipboard, Search
};

const GlobalSearch = ({
  setPage,
  offences = {},
  howToGuides = [],
  serviceProviders = { lawyers: [], insurance: [], mechanics: [], evaluators: [] },
  speedCameras = [],
  navigateToOffence,
  lang = 'en'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Define all searchable pages
  const pages = [
    { id: 'home', title: 'Home', description: 'Main page with all features', icon: 'Home', category: 'Pages' },
    { id: 'askbot', title: 'Ask Bot', description: 'AI assistant for traffic questions', icon: 'MessageCircle', category: 'Pages', keywords: ['chat', 'help', 'question', 'ask'] },
    { id: 'offences', title: 'Offences & Fines', description: 'Browse all traffic offences and penalties', icon: 'Scale', category: 'Pages', keywords: ['fine', 'penalty', 'violation', 'ticket'] },
    { id: 'calculator', title: 'Fine Calculator', description: 'Calculate total fines for offences', icon: 'Calculator', category: 'Pages', keywords: ['calculate', 'total', 'cost'] },
    { id: 'roadcode', title: 'Road Code', description: 'Jamaica Road Code rules and regulations', icon: 'BookOpen', category: 'Pages', keywords: ['rules', 'regulations', 'law', 'signs', 'road code'] },
    { id: 'online-services', title: 'Registration & Fitness Renewal', description: 'Renew and retrieve your motor vehicle documents online via TAJ', icon: 'Car', category: 'Pages', keywords: ['renew', 'renewal', 'licence', 'fitness', 'registration', 'taj', 'online', 'retrieve', 'mvrc', 'emvrc', 'documents', 'certificate'] },
    { id: 'insurance-lookup', title: 'Insurance Lookup', description: 'Verify motor vehicle insurance coverage in Jamaica via IAJ', icon: 'Shield', category: 'Pages', keywords: ['insurance', 'verify', 'coverage', 'lookup', 'check', 'iaj', 'vehicle', 'motor', 'valid', 'insured'] },
    { id: 'walkthrough', title: 'Police Stop Guide', description: 'What to do when stopped by police', icon: 'Shield', category: 'Pages', keywords: ['police', 'stop', 'rights', 'pulled over'] },
    { id: 'howto', title: 'How-To Guides', description: 'Step-by-step guides for vehicle procedures', icon: 'Clipboard', category: 'Pages', keywords: ['guide', 'tutorial', 'steps', 'how'] },
    { id: 'fees', title: 'Fees', description: 'Government fees for vehicle services', icon: 'DollarSign', category: 'Pages', keywords: ['cost', 'price', 'pay'] },
    { id: 'learner', title: 'Learner Guide', description: 'Information for learner drivers', icon: 'GraduationCap', category: 'Pages', keywords: ['learner', 'permit', 'new driver', 'provisional'] },
    { id: 'tickets', title: 'Ticket Tracker', description: 'Track and manage your traffic tickets', icon: 'Receipt', category: 'Pages', keywords: ['ticket', 'track', 'manage', 'payment'] },
    { id: 'documents', title: 'My Documents', description: 'Store your driving documents', icon: 'FileText', category: 'Pages', keywords: ['document', 'licence', 'insurance', 'fitness'] },
    { id: 'accident', title: 'Accident Report', description: 'Report and document accidents', icon: 'AlertCircle', category: 'Pages', keywords: ['accident', 'crash', 'collision', 'report'] },
    { id: 'hotspots', title: 'Accident Hotspots', description: 'Dangerous road locations', icon: 'MapPin', category: 'Pages', keywords: ['danger', 'hotspot', 'dangerous', 'risk'] },
    { id: 'cameras', title: 'Speed Cameras', description: 'Speed camera locations in Jamaica', icon: 'Camera', category: 'Pages', keywords: ['camera', 'speed', 'photo'] },
    { id: 'traffic', title: 'Traffic Updates', description: 'Live traffic information', icon: 'Radio', category: 'Pages', keywords: ['traffic', 'congestion', 'road', 'update'] },
    { id: 'services', title: 'Service Providers', description: 'Find lawyers, mechanics, insurance', icon: 'Building2', category: 'Pages', keywords: ['service', 'provider', 'lawyer', 'mechanic', 'insurance'] },
    { id: 'getlisted', title: 'Get Listed', description: 'Add your business to our directory', icon: 'Send', category: 'Pages', keywords: ['list', 'business', 'advertise', 'directory'] },
    { id: 'resources', title: 'Resources', description: 'Useful links and resources', icon: 'Book', category: 'Pages', keywords: ['resource', 'link', 'external'] },
    { id: 'demerit', title: 'Demerit Points', description: 'Understand the demerit point system', icon: 'AlertTriangle', category: 'Pages', keywords: ['demerit', 'point', 'suspension'] },
    { id: 'settings', title: 'Settings', description: 'App settings and preferences', icon: 'Settings', category: 'Pages', keywords: ['setting', 'preference', 'config'] },
    { id: 'contact', title: 'Contact Us', description: 'Get in touch with us', icon: 'Send', category: 'Pages', keywords: ['contact', 'email', 'message', 'support'] },
    { id: 'privacy', title: 'Privacy Policy', description: 'Our privacy policy', icon: 'FileText', category: 'Pages', keywords: ['privacy', 'policy', 'data'] },
  ];

  // Build search index
  const buildSearchIndex = useCallback(() => {
    const index = [];

    // Add pages
    pages.forEach(page => {
      index.push({
        type: 'page',
        id: page.id,
        title: page.title,
        description: page.description,
        icon: page.icon,
        category: page.category || 'Pages',
        keywords: [page.title.toLowerCase(), page.description.toLowerCase(), ...(page.keywords || [])].join(' '),
        action: page.external
          ? () => window.open(page.external, '_blank', 'noopener,noreferrer')
          : () => setPage(page.id)
      });
    });

    // Add offences
    Object.entries(offences).forEach(([code, offence]) => {
      // Build keywords from title, shortTitle, details, and keywords array
      const keywordParts = [
        code.toLowerCase(),
        (offence.title || '').toLowerCase(),
        (offence.shortTitle || '').toLowerCase(),
        (offence.details || '').toLowerCase(),
        (offence.category || '').toLowerCase(),
        ...(offence.keywords || []).map(k => k.toLowerCase()),
        'fine', 'offence', 'ticket', 'penalty'
      ];

      index.push({
        type: 'offence',
        id: code,
        title: offence.title || offence.shortTitle || code,
        description: `${offence.fine} | ${offence.points || 0} points`,
        icon: 'Scale',
        category: 'Offences',
        keywords: keywordParts.join(' '),
        action: () => {
          // Use navigateToOffence if available to open the specific offence
          if (navigateToOffence) {
            navigateToOffence(code, offence.category);
          } else {
            setPage('offences');
          }
        },
        data: { code, ...offence }
      });
    });

    // Add How-To Guides
    howToGuides.forEach(guide => {
      index.push({
        type: 'guide',
        id: guide.id,
        title: guide.title,
        description: guide.subtitle || guide.category,
        icon: 'Clipboard',
        category: 'How-To Guides',
        keywords: [
          guide.title.toLowerCase(),
          (guide.subtitle || '').toLowerCase(),
          (guide.category || '').toLowerCase(),
          'guide', 'how to', 'steps'
        ].join(' '),
        action: () => setPage('howto'),
        data: guide
      });
    });

    // Add Service Providers
    const addServiceProviders = (providers, type, iconName) => {
      (providers || []).forEach(provider => {
        index.push({
          type: 'service',
          id: `${type}-${provider.id}`,
          title: provider.name,
          description: `${provider.parish} | ${provider.services || type}`,
          icon: iconName,
          category: 'Service Providers',
          keywords: [
            provider.name.toLowerCase(),
            (provider.parish || '').toLowerCase(),
            (provider.services || '').toLowerCase(),
            (provider.specialty || '').toLowerCase(),
            type.toLowerCase(),
            'service', 'provider'
          ].join(' '),
          action: () => setPage('services'),
          data: { type, ...provider }
        });
      });
    };

    addServiceProviders(serviceProviders.lawyers, 'Lawyer', 'Scale');
    addServiceProviders(serviceProviders.insurance, 'Insurance', 'Shield');
    addServiceProviders(serviceProviders.mechanics, 'Mechanic', 'Car');
    addServiceProviders(serviceProviders.evaluators, 'Vehicle Evaluator', 'Search');

    // Add Speed Cameras
    (speedCameras || []).forEach((camera, idx) => {
      index.push({
        type: 'camera',
        id: `camera-${idx}`,
        title: camera.location || camera.name,
        description: `Speed Limit: ${camera.speedLimit || camera.limit || 'N/A'} km/h`,
        icon: 'Camera',
        category: 'Speed Cameras',
        keywords: [
          (camera.location || camera.name || '').toLowerCase(),
          (camera.parish || '').toLowerCase(),
          'camera', 'speed', 'limit'
        ].join(' '),
        action: () => setPage('cameras'),
        data: camera
      });
    });

    return index;
  }, [offences, howToGuides, serviceProviders, speedCameras, setPage, navigateToOffence]);

  // Memoized search index
  const searchIndex = useMemo(() => buildSearchIndex(), [buildSearchIndex]);

  // Fuse.js instance for fuzzy search
  const fuse = useMemo(() => new Fuse(searchIndex, {
    keys: [
      { name: 'title', weight: 0.4 },
      { name: 'keywords', weight: 0.3 },
      { name: 'description', weight: 0.2 },
      { name: 'category', weight: 0.1 }
    ],
    threshold: 0.4, // Allow fuzzy matching (0 = exact, 1 = match anything)
    distance: 100,
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 2
  }), [searchIndex]);

  // Fuzzy search function with improved relevance
  const search = useCallback((searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    const q = searchQuery.toLowerCase().trim();

    // Use Fuse.js for fuzzy search
    const fuseResults = fuse.search(q);

    // Map results and add relevance info
    const matches = fuseResults.map(result => ({
      ...result.item,
      score: result.score
    }));

    // Sort: exact title matches first, then pages, then by score
    matches.sort((a, b) => {
      const aTitle = (a.title || '').toLowerCase();
      const bTitle = (b.title || '').toLowerCase();
      const aExact = aTitle.includes(q) ? 1 : 0;
      const bExact = bTitle.includes(q) ? 1 : 0;

      // Exact title matches first
      if (aExact !== bExact) return bExact - aExact;

      // Pages prioritized over other types
      if (a.type === 'page' && b.type !== 'page') return -1;
      if (b.type === 'page' && a.type !== 'page') return 1;

      // Then by score (lower is better)
      return (a.score || 0) - (b.score || 0);
    });

    setResults(matches.slice(0, 15)); // Show more results with fuzzy search
    setSelectedIndex(0);
  }, [fuse]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => search(query), 150);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Open search with Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }

      // Close with Escape
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  // Handle result selection
  const handleSelect = (result) => {
    result.action();
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  // Get icon component
  const getIcon = (iconName) => {
    const IconComponent = ICONS[iconName] || Search;
    return IconComponent;
  };

  // Category colors
  const getCategoryColor = (category) => {
    switch (category) {
      case 'Pages': return 'bg-emerald-100 text-emerald-700';
      case 'Offences': return 'bg-red-100 text-red-700';
      case 'How-To Guides': return 'bg-blue-100 text-blue-700';
      case 'Service Providers': return 'bg-purple-100 text-purple-700';
      case 'Speed Cameras': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Search Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white min-h-[44px]"
        aria-label="Open search (Ctrl+K)"
        aria-haspopup="dialog"
      >
        <Search className="w-4 h-4" aria-hidden="true" />
        <span className="hidden md:inline text-sm">Search</span>
        <kbd className="hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-white/20 rounded" aria-hidden="true">
          ⌘K
        </kbd>
      </button>

      {/* Search Modal Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-[15vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="search-dialog-title"
        >
          <div className="w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b">
              <Search className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
              <label htmlFor="global-search-input" className="sr-only" id="search-dialog-title">Search the site</label>
              <input
                id="global-search-input"
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search pages, offences, guides, services..."
                className="flex-1 text-lg outline-none bg-transparent text-gray-900 placeholder:text-gray-400"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-controls="search-results"
                aria-expanded={results.length > 0}
                aria-activedescendant={results.length > 0 ? `search-result-${selectedIndex}` : undefined}
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 hover:bg-gray-100 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4 text-gray-400" aria-hidden="true" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="px-2 py-1 text-xs text-gray-500 bg-gray-100 rounded hover:bg-gray-200 min-h-[44px]"
                aria-label="Close search"
              >
                ESC
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto" id="search-results" role="listbox" aria-label="Search results">
              {results.length > 0 ? (
                <div className="py-2">
                  {results.map((result, index) => {
                    const IconComponent = getIcon(result.icon);
                    return (
                      <button
                        key={result.id}
                        id={`search-result-${index}`}
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setSelectedIndex(index)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors min-h-[56px] ${
                          index === selectedIndex ? 'bg-emerald-50' : 'hover:bg-gray-50'
                        }`}
                        role="option"
                        aria-selected={index === selectedIndex}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          index === selectedIndex ? 'bg-emerald-100' : 'bg-gray-100'
                        }`} aria-hidden="true">
                          <IconComponent className={`w-5 h-5 ${
                            index === selectedIndex ? 'text-emerald-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{result.title}</span>
                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${getCategoryColor(result.category)}`}>
                              {result.category}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">{result.description}</p>
                        </div>
                        <ArrowRight className={`w-4 h-4 flex-shrink-0 ${
                          index === selectedIndex ? 'text-emerald-500' : 'text-gray-300'
                        }`} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              ) : query ? (
                <div className="py-12 text-center text-gray-500" role="status" aria-live="polite">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" aria-hidden="true" />
                  <p className="font-medium">No results found</p>
                  <p className="text-sm">Try searching for pages, offences, or services</p>
                </div>
              ) : (
                <div className="py-6 px-4">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3" id="quick-links-label">Quick Links</p>
                  <div className="grid grid-cols-2 gap-2" role="group" aria-labelledby="quick-links-label">
                    {[
                      { id: 'offences', title: 'Offences & Fines', icon: Scale },
                      { id: 'calculator', title: 'Fine Calculator', icon: Calculator },
                      { id: 'howto', title: 'How-To Guides', icon: Clipboard },
                      { id: 'services', title: 'Service Providers', icon: Building2 },
                      { id: 'tickets', title: 'Ticket Tracker', icon: Receipt },
                      { id: 'askbot', title: 'Ask Bot', icon: MessageCircle },
                    ].map(item => (
                      <button
                        key={item.id}
                        onClick={() => { setPage(item.id); setIsOpen(false); }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-emerald-50 text-gray-700 hover:text-emerald-700 transition-colors text-sm min-h-[44px]"
                      >
                        <item.icon className="w-4 h-4" aria-hidden="true" />
                        <span>{item.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-xs text-gray-500" aria-hidden="true">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↑</kbd>
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↓</kbd>
                  to navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↵</kbd>
                  to select
                </span>
              </div>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-200 rounded">esc</kbd>
                to close
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
